// The design lock for sound. Everything is made on the spot with Web Audio;
// there are no sound files. Pitches follow the Bohlen–Pierce scale: thirteen
// equal steps to the tritave (3:1), a scale built on three for a people who
// count in threes. A card's pitch is the number it spells, so a run sounds
// like a falling figure and the three cards of a fold an even-stepped chord.

export type Bell = { readonly gain: number; readonly length: number; readonly bright: number }

export type SoundConfig = {
  readonly base: number // hertz: the pitch of zero
  readonly steps: number // equal steps in one tritave
  readonly master: number
  readonly compressor: { readonly threshold: number; readonly ratio: number }
  readonly room: { readonly seconds: number; readonly decay: number; readonly wet: number }
  readonly drone: {
    readonly gain: number
    readonly fadeIn: number
    readonly lowpass: number
    // [multiple of the drone's root, detune in cents, gain]
    readonly partials: readonly (readonly [number, number, number])[]
  }
  readonly lift: { readonly seconds: number; readonly band: number; readonly q: number; readonly gain: number }
  readonly rest: Bell
  readonly hold: Bell
  readonly fold: { readonly note: Bell; readonly spread: number; readonly landing: Bell; readonly over: Bell }
  readonly turn: { readonly notes: number; readonly gap: number; readonly bell: Bell }
  readonly refuse: { readonly pitches: readonly (readonly [number, number])[]; readonly seconds: number }
  readonly monad: { readonly gap: number; readonly note: Bell; readonly chord: readonly number[]; readonly chordBell: Bell }
  readonly chime: Bell
}

export const SOUND = {
  base: 262,
  steps: 13,
  master: 0.8,
  compressor: { threshold: -18, ratio: 3 },
  room: { seconds: 3.2, decay: 2.6, wet: 0.42 },
  drone: {
    gain: 0.05,
    fadeIn: 6,
    lowpass: 420,
    partials: [
      [1, -3, 0.5],
      [1, 4, 0.5],
      [3, 0, 0.22],
      [3 ** (6 / 13), 2, 0.14],
    ],
  },
  lift: { seconds: 0.05, band: 2600, q: 3, gain: 0.18 },
  rest: { gain: 0.2, length: 2.4, bright: 1 },
  hold: { gain: 0.12, length: 1.4, bright: 0.4 },
  fold: {
    note: { gain: 0.16, length: 2.6, bright: 1 },
    spread: 0.09,
    landing: { gain: 0.26, length: 4.2, bright: 1.4 },
    over: { gain: 0.07, length: 3, bright: 0.6 },
  },
  turn: { notes: 7, gap: 0.05, bell: { gain: 0.06, length: 1.2, bright: 1 } },
  refuse: {
    pitches: [
      [92, 0.3],
      [97.5, 0.22],
    ],
    seconds: 0.3,
  },
  monad: { gap: 0.06, note: { gain: 0.07, length: 3, bright: 1 }, chord: [0, 13, 26, -13], chordBell: { gain: 0.16, length: 7, bright: 1.2 } },
  chime: { gain: 0.1, length: 2.8, bright: 0.7 },
} as const satisfies SoundConfig
